import { z } from "zod";

import {
	AgeGroupEnum,
	CompetitionAdvancingStyles,
	CurrencyCode,
	baseCreateType,
	mediaModelResolver
} from "shared/types";

import { CompetitionTypes, TieBreakerEnums, competitionCustomFeeResolver } from "./CompetitionModel";
import { logoResolver } from "./CreateTypes";
import { GenderEnum } from "./GenderEnum";
import { partialPaymentSettingsResolver, splitPaymentSettingsResolver } from "./PaymentModel";
import { setupCategoryObject } from "./SetupEntityModel";
import { WaiverFormat } from "./Waiver";

export const tieBreakerRuleModel = z.object({
	id: z.string().optional(),
	type: z.lazy(() => z.nativeEnum(TieBreakerEnums)),
	ord: z.number(),
	settings: z
		.object({
			maxGoalDifferential: z.number().nullable()
		})
		.optional()
		.nullable()
});

export type TieBreakerRuleModel = z.infer<typeof tieBreakerRuleModel>;

export const setupCompetitionObject = z.object({
	winPoints: z.number().nullable(),
	drawPoints: z.number().nullable(),
	lossPoints: z.number().nullable(),
	tieBreakerRules: tieBreakerRuleModel.array(),
	ages: z.array(setupCategoryObject).optional(),
	genders: z.array(z.nativeEnum(GenderEnum)).optional(),
	levels: z.array(z.object({ name: z.string(), description: z.string().optional() })),
	competitionId: z.string().optional(),
	advancingStyle: z.lazy(() => z.nativeEnum(CompetitionAdvancingStyles).optional())
});

export type SetupCompetitionModel = z.infer<typeof setupCompetitionObject>;

export enum CompetitionRegistrationTypes {
	OPEN = "OPEN",
	REGULAR = "REGULAR"
}

export enum CompetitionOpenRegistrationTypes {
	PLAYER = "PLAYER",
	TEAM = "TEAM"
}

export enum CompetitionFeePaidByTypes {
	PLAYER = "PLAYER",
	TEAM = "TEAM"
}

export const createCompetitionObject = setupCompetitionObject.extend({
	logo: logoResolver.nullable(),
	location: z.object({ value: z.string(), label: z.string() }),
	leagueId: z.string(),
	type: z.lazy(() => z.nativeEnum(CompetitionTypes)),
	name: z.string(),
	description: z.string().nullable(),
	startsOn: z.string(),
	endsOn: z.string(),
	registrationEndsOn: z.string().nullable(),
	paymentEndsOn: z.string().nullable(),
	website: z.string().nullable(),
	rules: z.string().nullable(),
	private: z.boolean(),
	autoPublishAdvancements: z.boolean(),
	goalDifferential: z.number().nullable(),
	registrationType: z.nativeEnum(CompetitionRegistrationTypes),
	openRegistrationType: z.nativeEnum(CompetitionOpenRegistrationTypes),
	registrationFee: z.number().nullable(),
	feePaidBy: z.nativeEnum(CompetitionFeePaidByTypes).optional().nullable(),
	registrationFeeCurrency: z.nativeEnum(CurrencyCode).optional().nullable(),
	feeTaxPaidByClient: z.boolean().nullable(),
	playerTag: z.string().optional().nullable(),
	club: baseCreateType.pick({ id: true, name: true }).optional().nullable(),
	partialPayment: z
		.lazy(() => partialPaymentSettingsResolver)
		.optional()
		.nullable(),
	splitPayment: splitPaymentSettingsResolver.array().optional(),
	enableCustomFees: z.boolean(),
	customFees: z.lazy(() =>
		competitionCustomFeeResolver
			.partial()
			.omit({ id: true })
			.extend({
				level: z.string().optional()
			})
			.array()
	),
	divisions: z
		.object({
			name: z.string(),
			age: z.object({
				behavior: z.nativeEnum(AgeGroupEnum),
				value: z.number()
			}),
			gender: z.nativeEnum(GenderEnum),
			level: z.string(),
			matchSettings: z.object({
				matchFormat: z.string(),
				duration: z.number(),
				matchBuffer: z.number(),
				refereePerGame: z.number(),
				includeHalftime: z.boolean()
			}),
			tieBreakerRules: tieBreakerRuleModel.array().optional()
		})
		.array()
		.optional(),
	waivers: z
		.object({
			file: mediaModelResolver.or(z.string().nullable()).nullable().optional(),
			format: z.nativeEnum(WaiverFormat),
			link: z.string().nullable().optional(),
			title: z.string(),
			dueDateOn: z.string().nullable().optional()
		})
		.array()
		.optional()
});

export type CreateCompetitionObject = z.infer<typeof createCompetitionObject>;
